import {Observable} from 'rxjs';
import {
    distinctUntilChanged,
    filter,
    map,
    pairwise,
    withLatestFrom
} from 'rxjs/operators';

import SyntheticMediaEventType   from '../../../../Solutions/Shared/Constants/SyntheticMediaEventType';
import IMediaEventsStreamPayload from '../../../../Solutions/Shared/Interfaces/IMediaEventsStreamPayload';
import BufferingType             from '../Constants/BufferingType';
import Playback                  from '../Playback';

const MIN_SEEK_THRESHOLD = 1;
const MAX_STALLED_THRESHOLD = 0.1;
const EIGHT_HOURS = 8 * 60 * 60;

/**
 * Creates and returns a function which determines whether buffering
 * (if ocurring) was induced by either a network issue, or the user
 * seeking.
 *
 * This is done by looking for a jump in `currentTime`, greater
 * than a second, before buffering begins.
 *
 * If the jump was greater than the maximum seek range (8 hours), then
 * this more likely due to the initial `currentTime` read on a live/linear
 * stream which uses a unix timestamp as its currentTime.
 */

const createDeltaParser = () => {
    let didSeek = false;

    return (delta: number): BufferingType => {
        const absDelta = Math.abs(delta);

        if (
            absDelta < MAX_STALLED_THRESHOLD || absDelta > EIGHT_HOURS
        ) return didSeek ? BufferingType.SEEK : BufferingType.NETWORK;

        didSeek = absDelta > MIN_SEEK_THRESHOLD && absDelta < EIGHT_HOURS;

        return BufferingType.NONE;
    };
};

/**
 * Filters out emissions where the currentTime is less than the provided
 * `startTime` (if loading), to prevent erroneous PLAYING state while
 * still LOADING.
 */

const filterStartFromResumePoint = ([{state}, {media, isLoading}]) =>
    !isLoading || media.startTime === 0 || (isLoading && media.startTime > 0 && state.currentTime >= media.startTime);

const createBufferingStream = (
    mediaEvents$: Observable<IMediaEventsStreamPayload>,
    playbackStoreUpdate$: Observable<Playback>
): Observable<BufferingType> =>
    mediaEvents$
        .pipe(
            // Combine throughput with latest value from
            // playback store
            withLatestFrom(playbackStoreUpdate$),
            // Filter out any emissions from media events of
            // types other than `TIME_UPDATE`, or when the player is not
            // playing or buffering
            filter(([{type}, {isPlaying, isBuffering, isLoading}]) => (
                type === SyntheticMediaEventType.TIME_UPDATE &&
                (isPlaying || isBuffering || isLoading)
            )),
            // See `filterStartFromResumePoint` function description
            filter(filterStartFromResumePoint),
            // Map the throughput to the solution's current time
            map(([{state}]) => state.currentTime),
            // After the first emission, map throughput to a tuple of
            // the current and previous values
            pairwise(),
            // Map throughput to the difference in seconds between said
            // values
            map(([prev, next]) => (next - prev)),
            // Map to the appropriate buffering type where a non-zero delta
            // represents playback progress, and a zero delta represents
            // playback stalled (buffering)
            map(createDeltaParser()),
            // Only emit when buffering starts or stops
            distinctUntilChanged()
        );

export default createBufferingStream;